/**
 * Pure, store-independent helpers for shard selection, viewport intersection,
 * and LRU eviction. Nothing here calls get() or set() — all inputs are
 * explicit arguments and outputs are plain data.
 */

import { Structure, MapClaimSummary } from '../../types';

// ---------------------------------------------------------------------------
// Types (mirrored here so this module has no dependency on structuresStore.ts)
// ---------------------------------------------------------------------------

export interface ViewportBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface ShardBbox {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SHARD_FETCH_CONCURRENCY = 8;

// Cap total in-memory structures so panning across big metros doesn't OOM
// the tab. Supercluster's index + diff-based marker rendering keeps render
// cost independent of total point count, so the ceiling can be generous;
// memory is the real constraint. ~150k structures ≈ 60MB.
export const MAX_CACHED_STRUCTURES = 150_000;

// Cap per-call shard fetches so a wide viewport that technically intersects
// a thousand shards (e.g., zoomed out over Leon County's 1047 tiles)
// doesn't queue them all serially. The closest N to the viewport center
// are fetched first; the user sees a representative cluster quickly,
// subsequent pans/zooms pull the rest on demand.
// Lowered from 120 → 60 on first-load pain for dense areas like SF
// (~225k structures across 232 shards). Halving the initial batch roughly
// halves both network wait and on-thread JSON/object work; later pans fill
// the rest. Clustering collapses the difference at wide zoom anyway.
export const MAX_SHARDS_PER_LOAD = 60;

// Ceiling on an area's manifest `totalStructures` for the search preload
// (ensureAreaLoadedForSearch in structuresStore.ts) to sweep the rest of an
// area's shards in the background. Big Bear is ~13k structures today and
// ~25k once Big Bear City and Sugarloaf are added — comfortably under. San
// Francisco is ~224k across 232 shards; preloading it whole would mean
// hundreds of shard fetches and blow well past MAX_CACHED_STRUCTURES, so it
// must never preload — above this ceiling ensureAreaLoadedForSearch is a
// no-op.
export const SEARCH_PRELOAD_MAX_STRUCTURES = 40_000;

// ---------------------------------------------------------------------------
// Claim helpers
// ---------------------------------------------------------------------------

function progressPercentFromClaim(claim: MapClaimSummary): number | undefined {
  const snap = claim.progressSnapshot;
  if (!snap || snap.total <= 0) return undefined;
  return Math.round((snap.completed / snap.total) * 100);
}

export function joinClaim(structure: Structure, claim: MapClaimSummary | undefined): Structure {
  if (!claim) {
    return structure.claimSummary ? { ...structure, claimSummary: undefined } : structure;
  }
  return {
    ...structure,
    claimSummary: {
      claimedBy: claim.claimedBy,
      progressPercent: progressPercentFromClaim(claim),
    },
  };
}

// ---------------------------------------------------------------------------
// Shard key helpers
// ---------------------------------------------------------------------------

export function shardKey(areaId: string, shardId: string): string {
  return `${areaId}:${shardId}`;
}

export function findStructureInShards(
  byShard: Record<string, Structure[]>,
  structureId: string
): Structure | null {
  for (const key in byShard) {
    const arr = byShard[key];
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].id === structureId) return arr[i];
    }
  }
  return null;
}

export function findUserAdded(list: Structure[], structureId: string): Structure | null {
  for (let i = 0; i < list.length; i++) {
    if (list[i].id === structureId) return list[i];
  }
  return null;
}

// ---------------------------------------------------------------------------
// Viewport / bbox helpers
// ---------------------------------------------------------------------------

export function bboxIntersects(a: ShardBbox, v: ViewportBox): boolean {
  return a.minLat <= v.north && a.maxLat >= v.south && a.minLng <= v.east && a.maxLng >= v.west;
}

export function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Candidate selection (pure): choose which shards to fetch for a viewport
// ---------------------------------------------------------------------------

export interface ShardCandidate {
  areaId: string;
  shardId: string;
  key: string;
}

interface ShardMeta {
  shardId: string;
  bbox: ShardBbox;
}

interface AreaManifestLike {
  shards: ShardMeta[];
}

/**
 * Given the current manifests, the already-loaded shard order, the in-flight
 * set, and a viewport, return:
 *   - `toFetch`:   up to MAX_SHARDS_PER_LOAD candidates ordered by distance
 *                  from viewport centre (closest first)
 *   - `touchedKeys`: keys that are already loaded AND intersect the viewport
 *                    (used by the store to bump their LRU position)
 */
export function selectShardCandidates(args: {
  areaIds: string[];
  manifests: Record<string, AreaManifestLike>;
  loadedShardOrder: string[];
  inflightShards: ReadonlySet<string>;
  viewport: ViewportBox;
}): { toFetch: ShardCandidate[]; touchedKeys: string[] } {
  const { areaIds, manifests, loadedShardOrder, inflightShards, viewport } = args;
  const viewportCenterLat = (viewport.south + viewport.north) / 2;
  const viewportCenterLng = (viewport.west + viewport.east) / 2;
  const loadedSet = new Set(loadedShardOrder);

  const candidates: (ShardCandidate & { distance: number })[] = [];
  const touchedKeys: string[] = [];

  for (const areaId of areaIds) {
    const manifest = manifests[areaId];
    if (!manifest) continue;
    for (const shard of manifest.shards) {
      if (!bboxIntersects(shard.bbox, viewport)) continue;
      const key = shardKey(areaId, shard.shardId);
      if (loadedSet.has(key)) {
        touchedKeys.push(key);
        continue;
      }
      if (inflightShards.has(key)) continue;
      const cLat = (shard.bbox.minLat + shard.bbox.maxLat) / 2;
      const cLng = (shard.bbox.minLng + shard.bbox.maxLng) / 2;
      const dLat = cLat - viewportCenterLat;
      const dLng = cLng - viewportCenterLng;
      candidates.push({ areaId, shardId: shard.shardId, key, distance: dLat * dLat + dLng * dLng });
    }
  }

  candidates.sort((a, b) => a.distance - b.distance);
  const toFetch = candidates.slice(0, MAX_SHARDS_PER_LOAD).map(({ areaId, shardId, key }) => ({
    areaId,
    shardId,
    key,
  }));

  return { toFetch, touchedKeys };
}

/**
 * All of one area's shards that are neither already loaded nor in flight, in
 * manifest order. Used by the search preload (ensureAreaLoadedForSearch in
 * structuresStore.ts) to sweep an entire area regardless of viewport —
 * unlike selectShardCandidates there's no distance sort or built-in cap; the
 * caller slices the result into batches of at most MAX_SHARDS_PER_LOAD so a
 * 91-shard area still fetches at a bounded rate per batch.
 */
export function selectMissingAreaShards(args: {
  areaId: string;
  manifest: AreaManifestLike;
  loadedShardOrder: string[];
  inflightShards: ReadonlySet<string>;
}): ShardCandidate[] {
  const { areaId, manifest, loadedShardOrder, inflightShards } = args;
  const loadedSet = new Set(loadedShardOrder);
  const out: ShardCandidate[] = [];
  for (const shard of manifest.shards) {
    const key = shardKey(areaId, shard.shardId);
    if (loadedSet.has(key) || inflightShards.has(key)) continue;
    out.push({ areaId, shardId: shard.shardId, key });
  }
  return out;
}

// ---------------------------------------------------------------------------
// LRU eviction (pure): decide which shards to drop given a cache-size ceiling
// ---------------------------------------------------------------------------

/**
 * Given the current shard map, the LRU order array, newly committed keys,
 * and viewport centre, return a new `{byShard, order}` pair with farther
 * shards evicted until the total structure count is within
 * `maxCachedStructures`.
 *
 * The caller is responsible for applying the returned values to state.
 */
export function evictToLimit(args: {
  byShard: Record<string, Structure[]>;
  currentOrder: string[];
  newKeys: string[];
  viewportCenterLat: number;
  viewportCenterLng: number;
  manifests: Record<string, AreaManifestLike>;
  maxCachedStructures?: number;
}): { byShard: Record<string, Structure[]>; order: string[] } {
  const {
    byShard,
    currentOrder,
    newKeys,
    viewportCenterLat,
    viewportCenterLng,
    manifests,
    maxCachedStructures = MAX_CACHED_STRUCTURES,
  } = args;

  const newKeySet = new Set(newKeys);
  // Build the new LRU order: existing keys (minus new ones) then new keys appended.
  const nextOrder = currentOrder.filter((k) => !newKeySet.has(k));
  for (const k of newKeys) nextOrder.push(k);

  const nextByShard = { ...byShard };

  const shardDistance = (key: string): number => {
    const colon = key.indexOf(':');
    if (colon < 0) return Infinity;
    const areaId = key.slice(0, colon);
    const shardId = key.slice(colon + 1);
    const manifest = manifests[areaId];
    if (!manifest) return Infinity;
    const shard = manifest.shards.find((s) => s.shardId === shardId);
    if (!shard) return Infinity;
    const cLat = (shard.bbox.minLat + shard.bbox.maxLat) / 2;
    const cLng = (shard.bbox.minLng + shard.bbox.maxLng) / 2;
    const dLat = cLat - viewportCenterLat;
    const dLng = cLng - viewportCenterLng;
    return dLat * dLat + dLng * dLng;
  };

  let total = 0;
  for (const k in nextByShard) total += nextByShard[k].length;

  if (total > maxCachedStructures) {
    const byDistance = Array.from(Object.keys(nextByShard)).sort(
      (a, b) => shardDistance(b) - shardDistance(a)
    );
    let i = 0;
    while (total > maxCachedStructures && i < byDistance.length - 1) {
      const key = byDistance[i++];
      const evicted = nextByShard[key];
      if (!evicted) continue;
      total -= evicted.length;
      delete nextByShard[key];
    }
    // Rebuild order with surviving keys, farthest first so future
    // evictions keep picking the right thing.
    const survivors = new Set(Object.keys(nextByShard));
    nextOrder.length = 0;
    for (const k of byDistance) if (survivors.has(k)) nextOrder.push(k);
  }

  return { byShard: nextByShard, order: nextOrder };
}
